import { formatCurrency, paymentMethods } from '../pages/receptionist/receptionistData';

export const PAYMENT_METHOD_OPTIONS = paymentMethods;

export function emptyPaymentSplit(method = 'cash') {
  return { method, amount: '' };
}

export function defaultPaymentSplits() {
  return PAYMENT_METHOD_OPTIONS.map((m) => ({ method: m.key, amount: '' }));
}

export function normalizeSplitRows(splits) {
  return (splits || [])
    .map((row) => ({
      method: row.method || 'cash',
      amount: Math.max(0, Number(row.amount) || 0),
    }))
    .filter((row) => row.amount > 0);
}

export function sumPaymentSplits(splits) {
  return normalizeSplitRows(splits).reduce((sum, row) => sum + row.amount, 0);
}

export function paymentMethodLabel(key) {
  const found = PAYMENT_METHOD_OPTIONS.find((m) => m.key === key);
  return found?.label || String(key || '—');
}

export function methodKeyFromLabel(label) {
  const normalized = String(label || '').trim().toLowerCase();
  const found = PAYMENT_METHOD_OPTIONS.find(
    (m) => m.label.toLowerCase() === normalized || m.key === normalized,
  );
  return found?.key || normalized.replace(/\s+/g, '_');
}

export function combineMethodLabels(splits) {
  const labels = normalizeSplitRows(splits).map((row) => paymentMethodLabel(row.method));
  return labels.length ? labels.join(' + ') : '—';
}

export function formatPaymentSplitsSummary(splits) {
  const rows = normalizeSplitRows(splits);
  if (!rows.length) return '—';
  return rows
    .map((row) => `${paymentMethodLabel(row.method)}: ${formatCurrency(row.amount)}`)
    .join(' · ');
}

export function parsePaymentSplitsFromRow(row) {
  const raw = row?.paymentSplits ?? row?.payment_splits;
  if (Array.isArray(raw) && raw.length) {
    const normalized = normalizeSplitRows(raw);
    // If split rows exist but all amounts are 0, fall through to header amount
    if (normalized.length) return normalized;
  }
  const paid = Number(row?.amount) || Number(row?.totalPaid) || 0;
  if (paid > 0) {
    const method = methodKeyFromLabel(row?.method || row?.payment_method);
    return [{ method, amount: paid }];
  }
  return [];
}

export function splitAmountForMethod(row, methodKey) {
  const amount = parsePaymentSplitsFromRow(row)
    .find((split) => split.method === methodKey)?.amount;
  return Number(amount) || 0;
}

export function mergePaymentSplits(existing, additional) {
  const totals = new Map();
  normalizeSplitRows(existing).forEach((row) => {
    totals.set(row.method, (totals.get(row.method) || 0) + row.amount);
  });
  normalizeSplitRows(additional).forEach((row) => {
    totals.set(row.method, (totals.get(row.method) || 0) + row.amount);
  });
  return Array.from(totals.entries()).map(([method, amount]) => ({ method, amount }));
}

export function serializePaymentSplits(splits) {
  return normalizeSplitRows(splits).map(({ method, amount }) => ({ method, amount }));
}

export function resolveSplitPayment(totalAmount, paymentSplits) {
  const total = Math.max(0, Number(totalAmount) || 0);
  const paid = sumPaymentSplits(paymentSplits);
  const left = Math.max(0, total - paid);
  // Total 0 (free sale) is completed; otherwise pending until fully paid
  const status = total <= 0 || paid >= total ? 'completed' : 'pending';
  return { total, paid, left, status };
}
